/**
 * @module components/YAxisSetting/DualYAxisSetting
 * @description 折线图双 Y 轴 — 主/次轴配置（开关由 YAxisSetting/index 管理）
 */

import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { useQuerySelector } from '../../../hooks/deps';
import { toChartTypeEnum } from '../../../utils/toChartTypeEnum';
import { EMPTY_COLUMN_INFO_LIST } from '../../../utils/constants';
import { useDualYAxisValidation } from '../../../ChartSettingsPanel/DualYAxisValidationContext';
import DualAxisPair from '../../common/DualAxis/DualAxisPair';
import AxisNameField from '../../common/DualAxis/fields/AxisNameField';
import AxisRangeField from '../../common/DualAxis/fields/AxisRangeField';
import AxisMetricMultiField from '../../common/DualAxis/fields/AxisMetricMultiField';
import {
  getSafeArray,
  normalizeLineDualAxisFromStorage,
  toMetricKey,
} from '../../common/DualAxis/axisMetricUtils';
import { useDualAxisDraft } from '../../common/DualAxis/hooks/useDualAxisDraft';
import { useAxisMetricOptions } from '../../common/DualAxis/hooks/useAxisMetricOptions';
import type {
  ILineDualAxisConfig,
  TAxisKey,
  TAxisMetric,
} from '../../common/DualAxis/types';
import { useLineDualMetricLinkage } from './useLineDualMetricLinkage';
import '../../common/DualAxis/index.scss';
import './index.scss';

const DualYAxisSetting: React.FC = () => {
  const { formatMessage } = useIntl();
  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const rawColumnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );
  const columnMetrics = Array.isArray(rawColumnMetrics)
    ? (rawColumnMetrics as Record<string, unknown>[])
    : [];

  /** 双 Y 轴指标校验错误（由 Panel Confirm 写入面板局部 Context） */
  const { errors: dualYAxisErrors } = useDualYAxisValidation();

  const { primaryAxis, secondaryAxis, dispatchAxis, dispatchAxes } =
    useDualAxisDraft<ILineDualAxisConfig>({
      normalize: (raw) => normalizeLineDualAxisFromStorage(raw, chartType),
      mergeOnPatch: (patch) => ({ ...patch, chartType }),
    });

  const { metricValues, filterSelectOptions, getMetricsPayload } =
    useAxisMetricOptions(columnMetrics);

  const primaryMetricKeys = useMemo(
    () =>
      getSafeArray<TAxisMetric>(primaryAxis.metrics).map(item =>
        toMetricKey(item),
      ),
    [primaryAxis.metrics],
  );
  const secondaryMetricKeys = useMemo(
    () =>
      getSafeArray<TAxisMetric>(secondaryAxis.metrics).map(item =>
        toMetricKey(item),
      ),
    [secondaryAxis.metrics],
  );

  const handleMetricChange = useLineDualMetricLinkage({
    primaryAxis,
    secondaryAxis,
    metricValues,
    getMetricsPayload,
    chartType,
    dispatchAxes,
  });

  const errorKeyForAxis = (axisKey: TAxisKey): string | undefined =>
    axisKey === 'primaryYAxis'
      ? dualYAxisErrors?.primary
      : dualYAxisErrors?.secondary;

  const buildSectionContent = (
    axisKey: TAxisKey,
    axis: ILineDualAxisConfig,
    axisMetricKeys: string[],
  ) => {
    const axisError = errorKeyForAxis(axisKey);

    return (
      <>
        <AxisNameField
          value={axis.yAxisName || ''}
          onChange={(v) => dispatchAxis(axisKey, { yAxisName: v })}
        />
        <AxisMetricMultiField
          value={axisMetricKeys}
          options={filterSelectOptions}
          onChange={(keys) => handleMetricChange(axisKey, keys)}
          selectClassName={`dbl-dual-y-axis-filter-select${axisError ? ' dbl-dual-y-axis-filter-select-error' : ''}`}
          overlayClassName="dbl-dual-y-axis-filter-select-overlay"
        />
        {axisError ? (
          <div className="dbl-dual-y-axis-metric-error">
            {formatMessage({ id: axisError })}
          </div>
        ) : null}
        <AxisRangeField
          value={!!axis.yAxisRangeSwitch}
          onChange={(v) => dispatchAxis(axisKey, { yAxisRangeSwitch: v })}
        />
      </>
    );
  };

  const sections = [
    {
      key: 'primaryYAxis',
      title: formatMessage({ id: 'dbl_chart_primary_axis' }),
      content: buildSectionContent(
        'primaryYAxis',
        primaryAxis,
        primaryMetricKeys,
      ),
    },
    {
      key: 'secondYAxis',
      title: formatMessage({ id: 'dbl_chart_secondary_axis' }),
      content: buildSectionContent(
        'secondYAxis',
        secondaryAxis,
        secondaryMetricKeys,
      ),
    },
  ];

  return (
    <div className="dbl-dual-y-axis-content">
      <DualAxisPair sections={sections} />
    </div>
  );
};

export default React.memo(DualYAxisSetting);
